import { useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Sparkles, RoundedBox } from "@react-three/drei";

const Capsule = ({ position, rotation = [0, 0, 0], color = "#00E5FF", capColor = "#ffffff", scale = 1 }) => (
  <group position={position} rotation={rotation} scale={scale}>
    <mesh position={[0, 0.16, 0]}>
      <capsuleGeometry args={[0.11, 0.32, 8, 20]} />
      <meshStandardMaterial color={color} roughness={0.18} metalness={0.35} />
    </mesh>
    <mesh position={[0, -0.16, 0]}>
      <capsuleGeometry args={[0.112, 0.32, 8, 20]} />
      <meshStandardMaterial color={capColor} roughness={0.12} metalness={0.2} />
    </mesh>
  </group>
);

const Tablet = ({ position, rotation = [0, 0, 0], color = "#ffffff", scale = 1 }) => (
  <group position={position} rotation={rotation} scale={scale}>
    <mesh>
      <cylinderGeometry args={[0.3, 0.3, 0.09, 40]} />
      <meshStandardMaterial color={color} roughness={0.25} metalness={0.3} />
    </mesh>
    <mesh position={[0, 0.048, 0]} rotation={[0, 0, 0]}>
      <boxGeometry args={[0.5, 0.012, 0.035]} />
      <meshStandardMaterial color="#B0BEC5" roughness={0.3} metalness={0.5} />
    </mesh>
  </group>
);

const PillCluster = ({ position, scale = 1, speed = 0.4 }) => {
  const ref = useRef();
  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * speed;
  });
  return (
    <group position={position} scale={scale} ref={ref}>
      <Capsule position={[0, 0, 0]} rotation={[0.4, 0, 1.1]} color="#00E5FF" />
      <Capsule position={[0.75, 0.4, 0.15]} rotation={[-0.3, 0.5, -0.9]} color="#00BFA5" capColor="#e8fffa" />
      <Capsule position={[-0.6, 0.55, -0.2]} rotation={[0.9, -0.4, 0.5]} color="#0A192F" capColor="#dfe8f0" scale={0.85} />
      <Tablet position={[0.15, -0.7, 0.3]} rotation={[0.5, 0.3, 0.2]} />
      <Tablet position={[-0.35, -0.15, -0.45]} rotation={[-0.4, 0.8, 0.5]} color="#00BFA5" scale={0.7} />
    </group>
  );
};

const GlassBox = ({ position, size, rotation = [0.3, 0.5, 0] }) => (
  <RoundedBox args={size} radius={0.06} smoothness={4} position={position} rotation={rotation}>
    <meshPhysicalMaterial color="#ffffff" transparent opacity={0.34} roughness={0.08} metalness={0.15} clearcoat={1} />
  </RoundedBox>
);

const Rig = ({ children }) => {
  const ref = useRef();
  useFrame((state, delta) => {
    if (!ref.current) return;
    ref.current.rotation.y = THREE.MathUtils.damp(ref.current.rotation.y, state.pointer.x * 0.22, 2.5, delta);
    ref.current.rotation.x = THREE.MathUtils.damp(ref.current.rotation.x, -state.pointer.y * 0.14, 2.5, delta);
  });
  return <group ref={ref}>{children}</group>;
};

const Scene = () => (
  <Rig>
    <ambientLight intensity={1.1} />
    <directionalLight position={[6, 8, 5]} intensity={1.6} color="#ffffff" />
    <pointLight position={[-4, -3, 2]} intensity={12} color="#00E5FF" />
    <pointLight position={[4, 2, -3]} intensity={8} color="#00BFA5" />

    <Float speed={1.4} rotationIntensity={0.4} floatIntensity={1.6}>
      <PillCluster position={[-4.3, 1.7, -1.2]} scale={1.05} speed={0.35} />
    </Float>
    <Float speed={1.1} rotationIntensity={0.5} floatIntensity={1.8}>
      <PillCluster position={[3.6, -0.4, -2]} scale={0.8} speed={-0.5} />
    </Float>
    <Float speed={1.6} floatIntensity={2.2}>
      <Capsule position={[2.4, 2.2, -1.6]} rotation={[0.5, 0.2, 0.9]} color="#00BFA5" scale={1.1} />
    </Float>
    <Float speed={1.9} floatIntensity={2.4}>
      <Tablet position={[-2.4, -2.2, -1.4]} rotation={[0.7, 0.4, 0.1]} scale={0.9} />
    </Float>
    <Float speed={1.8} floatIntensity={2}>
      <GlassBox position={[3.1, 1.6, -1.4]} size={[0.9, 1.2, 0.28]} />
    </Float>
    <Float speed={1.5} floatIntensity={1.7}>
      <GlassBox position={[-3.0, -1.4, -0.8]} size={[1.1, 0.75, 0.3]} rotation={[-0.2, -0.5, 0.1]} />
    </Float>
    <Sparkles count={90} scale={[14, 8, 6]} size={2.2} speed={0.28} color="#00BFA5" opacity={0.5} />
    <Sparkles count={60} scale={[12, 7, 5]} size={1.6} speed={0.2} color="#0A192F" opacity={0.35} />
  </Rig>
);

export const HeroCanvas = () => (
  <div className="absolute inset-0 z-0" aria-hidden>
    <Canvas camera={{ position: [0, 0, 8], fov: 46 }} dpr={[1, 1.75]} gl={{ antialias: true, alpha: true }}>
      <Scene />
    </Canvas>
  </div>
);
